import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Image, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface ProductImage {
	id: string;
	product_id: string;
	image_url: string;
	is_primary: boolean;
}

interface Product {
	id: string;
	name: string;
	description: string;
	price: number;
	msrp: number | null;
	category_id: string;
	category_name: string;
	created_at: string;
	deleted?: boolean;
	images: ProductImage[];
}

export function SupplierProductList() {
	const { user } = useAuth();
	const { toast } = useToast();
	const navigate = useNavigate();
	const [products, setProducts] = useState<Product[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [productToDelete, setProductToDelete] = useState<string | null>(null);

	useEffect(() => {
		if (user) {
			fetchProducts();
		}
	}, [user]);

	const fetchProducts = async () => {
		try {
			setIsLoading(true);

			// Update query to include category name using join and filter out deleted products
			const { data: productsData, error: productsError } = await supabase
				.from("products")
				.select(
					`
          *,
          categories:category_id (
            name
          )
        `
				)
				.eq("supplier_id", user?.id)
				.eq("deleted", false) // Only fetch non-deleted products
				.order("created_at", { ascending: false });

			if (productsError) throw productsError;

			// Transform the data to match our interface
			const transformedProducts = productsData.map((product) => ({
				...product,
				category_name: product.categories?.name || "Unknown",
				images: [],
			}));

			// Fetch images for all products
			const productIds = transformedProducts.map((product) => product.id);

			if (productIds.length > 0) {
				const { data: imagesData, error: imagesError } = await supabase
					.from("product_images")
					.select("*")
					.in("product_id", productIds);

				if (imagesError) throw imagesError;

				// Combine products with their images
				const productsWithImages = transformedProducts.map(
					(product) => ({
						...product,
						images:
							imagesData.filter(
								(img) => img.product_id === product.id
							) || [],
					})
				);

				setProducts(productsWithImages);
			} else {
				setProducts(transformedProducts);
			}
		} catch (error) {
			console.error("Error fetching products:", error);
			toast({
				title: "Error",
				description: "Failed to load products. Please try again.",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleDeleteProduct = async () => {
		if (!productToDelete) return;

		try {
			// Soft delete the product by setting deleted = true instead of removing from DB
			const { error } = await supabase
				.from("products")
				.update({ deleted: true })
				.eq("id", productToDelete);

			if (error) throw error;

			// Update local state by removing the product from the list
			setProducts((prev) =>
				prev.filter((product) => product.id !== productToDelete)
			);

			toast({
				title: "Product deleted",
				description:
					"The product has been removed from the marketplace.",
			});
		} catch (error: any) {
			toast({
				title: "Error",
				description:
					error.message ||
					"Failed to delete product. Please try again.",
				variant: "destructive",
			});
		} finally {
			setProductToDelete(null);
		}
	};

	const getPrimaryImageUrl = (product: Product) => {
		const primaryImage = product.images.find((img) => img.is_primary);
		return (
			primaryImage?.image_url ||
			product.images[0]?.image_url ||
			"/placeholder.svg"
		);
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<h2 className="text-2xl font-semibold tracking-tight">
					Your Products
				</h2>
				<Button
					variant="accent"
					onClick={() => navigate("/dashboard/products/add")}>
					<Plus className="mr-2 h-4 w-4" />
					Add Product
				</Button>
			</div>

			{products.length === 0 ? (
				<div className="text-center py-10 border border-dashed rounded-lg">
					<Image className="mx-auto h-12 w-12 text-gray-400 mb-4" />
					<h3 className="text-lg font-medium mb-2">
						No products yet
					</h3>
					<p className="text-gray-500 mb-4">
						Get started by adding your first product to the
						marketplace.
					</p>
					<Button
						variant="accent"
						onClick={() => navigate("/dashboard/products/add")}>
						<Plus className="mr-2 h-4 w-4" />
						Add Product
					</Button>
				</div>
			) : (
				<div className="border rounded-lg overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-[80px]">
									Image
								</TableHead>
								<TableHead>Product</TableHead>
								<TableHead>Category</TableHead>
								<TableHead className="text-right">
									Price
								</TableHead>
								<TableHead className="text-right">
									MSRP
								</TableHead>
								<TableHead className="text-right">
									Actions
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{products.map((product) => (
								<TableRow key={product.id}>
									<TableCell>
										<div className="h-12 w-12 rounded bg-gray-100 overflow-hidden">
											<img
												src={getPrimaryImageUrl(
													product
												)}
												alt={product.name}
												className="h-full w-full object-cover"
											/>
										</div>
									</TableCell>
									<TableCell className="font-medium">
										{product.name}
									</TableCell>
									<TableCell>
										{product.category_name}
									</TableCell>
									<TableCell className="text-right">
										${product.price.toFixed(2)}
									</TableCell>
									<TableCell className="text-right">
										{product.msrp
											? `$${product.msrp.toFixed(2)}`
											: "-"}
									</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													navigate(
														`/dashboard/products/edit/${product.id}`
													)
												}>
												<Pencil className="h-4 w-4" />
											</Button>

											<AlertDialog>
												<AlertDialogTrigger asChild>
													<Button
														variant="ghost"
														size="sm"
														className="text-red-500 hover:text-red-600 hover:bg-red-50"
														onClick={() =>
															setProductToDelete(
																product.id
															)
														}>
														<Trash2 className="h-4 w-4" />
													</Button>
												</AlertDialogTrigger>
												<AlertDialogContent>
													<AlertDialogHeader>
														<AlertDialogTitle>
															Delete Product
														</AlertDialogTitle>
														<AlertDialogDescription>
															Are you sure you
															want to delete "
															{product.name}"?
															This will remove it
															from the
															marketplace, but the
															data will be
															preserved.
														</AlertDialogDescription>
													</AlertDialogHeader>
													<AlertDialogFooter>
														<AlertDialogCancel>
															Cancel
														</AlertDialogCancel>
														<AlertDialogAction
															className="bg-red-500 hover:bg-red-600"
															onClick={
																handleDeleteProduct
															}>
															Delete
														</AlertDialogAction>
													</AlertDialogFooter>
												</AlertDialogContent>
											</AlertDialog>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}
